import React from 'react';
import { Check } from 'lucide-react';
import { inr, CalcLabel, CalcInput, Stepper, OptionCard } from './shared';

export const StepPackages = ({ config, selections, setSelections, basics }) => {
  const packages = config.venue_packages || [];
  const ppPackages = config.per_person_packages || [];
  const pick = (patch) => setSelections({ ...selections, package_id: null, per_person_package_id: null, ...patch });

  return (
    <div className="space-y-3" data-testid="calc-step-packages">
      <OptionCard
        selected={!selections.package_id && !selections.per_person_package_id}
        onClick={() => pick({})}
        testId="calc-package-none"
      >
        <p className="font-wvc-display text-lg text-[#0A1710]">Build my own celebration</p>
        <p className="text-xs text-[#1A3626]/60 mt-1 font-light">Choose event spaces, rooms and services individually.</p>
      </OptionCard>

      {packages.map(pkg => (
        <OptionCard
          key={pkg.id}
          selected={selections.package_id === pkg.id}
          onClick={() => pick({ package_id: pkg.id })}
          testId={`calc-package-${pkg.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-wvc-display text-lg text-[#0A1710]">{pkg.name}</p>
              <p className="text-xs text-[#1A3626]/60 mt-0.5 font-light">
                {pkg.duration_hours ? `${pkg.duration_hours} hours · ` : ''}
                {pkg.max_guests ? `up to ${pkg.max_guests} guests` : ''}
                {pkg.max_overnight_guests ? ` · ${pkg.max_overnight_guests} overnight` : ''}
              </p>
            </div>
            <p className="font-wvc-display text-lg text-[#1A3626] whitespace-nowrap">{inr(pkg.base_price)}</p>
          </div>
          {(pkg.included_facilities || []).length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
              {pkg.included_facilities.map((f, i) => (
                <span key={i} className="flex items-center gap-1 text-[11px] text-[#1A3626]/70">
                  <Check className="w-3 h-3 text-[#C6A87C]" /> {f}
                </span>
              ))}
            </div>
          )}
        </OptionCard>
      ))}

      {ppPackages.map(pp => (
        <OptionCard
          key={pp.id}
          selected={selections.per_person_package_id === pp.id}
          onClick={() => pick({ per_person_package_id: pp.id })}
          testId={`calc-pp-package-${pp.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-wvc-display text-lg text-[#0A1710]">{pp.name}</p>
              <p className="text-xs text-[#1A3626]/60 mt-0.5 font-light">
                Per-person package{pp.min_guests ? ` · min ${pp.min_guests} guests` : ''}
                {[pp.includes_stay && 'stay', pp.includes_food && 'food', pp.includes_venue && 'venue'].filter(Boolean).length > 0 &&
                  ` · includes ${[pp.includes_stay && 'stay', pp.includes_food && 'food', pp.includes_venue && 'venue'].filter(Boolean).join(', ')}`}
              </p>
            </div>
            <p className="font-wvc-display text-lg text-[#1A3626] whitespace-nowrap">{inr(pp.price_per_person)}<span className="text-xs">/person</span></p>
          </div>
        </OptionCard>
      ))}
    </div>
  );
};

export const StepSpaces = ({ config, selections, setSelections, result }) => {
  const spaces = config.event_spaces || [];
  const chosen = selections.event_spaces || [];
  const pkg = (config.venue_packages || []).find(p => p.id === selections.package_id);
  const allIncluded = pkg && (pkg.included_space_ids || []).includes('all');

  const toggle = (sp) => {
    const exists = chosen.find(c => c.space_id === sp.id);
    setSelections({
      ...selections,
      event_spaces: exists
        ? chosen.filter(c => c.space_id !== sp.id)
        : [...chosen, { space_id: sp.id, mode: sp.time_slots?.length ? 'slot' : 'full_day', slot_id: sp.time_slots?.[0]?.id, function_name: '' }]
    });
  };
  const patch = (spaceId, changes) => setSelections({
    ...selections,
    event_spaces: chosen.map(c => c.space_id === spaceId ? { ...c, ...changes } : c)
  });

  return (
    <div className="space-y-3" data-testid="calc-step-spaces">
      {allIncluded && (
        <p className="text-xs text-[#1A3626] bg-[#C6A87C]/15 border border-[#C6A87C]/40 px-4 py-3">
          All event spaces are included in your selected package. Pick the ones you plan to use for each function.
        </p>
      )}
      {spaces.map(sp => {
        const sel = chosen.find(c => c.space_id === sp.id);
        const included = allIncluded || (pkg?.included_space_ids || []).includes(sp.id);
        return (
          <div key={sp.id} className={`border transition-colors ${sel ? 'border-[#C6A87C] bg-white' : 'border-[#E5DCCF] bg-white'}`}>
            <button type="button" onClick={() => toggle(sp)} className="w-full text-left p-4 sm:p-5" data-testid={`calc-space-${sp.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-wvc-display text-lg text-[#0A1710]">{sp.name}</p>
                  <p className="text-xs text-[#1A3626]/60 mt-0.5 font-light">
                    {sp.setting === 'indoor' ? 'Indoor' : sp.setting === 'outdoor' ? 'Outdoor' : 'Semi-indoor'}
                    {sp.floating_capacity ? ` · up to ${sp.floating_capacity} floating` : ''}
                    {sp.seated_capacity ? ` · ${sp.seated_capacity} seated` : ''}
                  </p>
                  {(sp.suitable_events || []).length > 0 && (
                    <p className="text-[11px] text-[#C6A87C] mt-1 uppercase tracking-[0.12em]">{sp.suitable_events.join(' · ')}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {included
                    ? <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#1A3626] text-white px-2 py-1">Included</span>
                    : <p className="font-wvc-display text-[#1A3626]">{sp.full_day_price ? inr(sp.full_day_price) : sp.price_per_hour ? `${inr(sp.price_per_hour)}/hr` : ''}</p>}
                  <div className={`w-5 h-5 border mt-2 ml-auto flex items-center justify-center ${sel ? 'bg-[#1A3626] border-[#1A3626]' : 'border-[#E5DCCF]'}`}>
                    {sel && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                </div>
              </div>
            </button>
            {sel && (
              <div className="px-4 sm:px-5 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-[#E5DCCF]/60 pt-4">
                <div>
                  <CalcLabel>Function</CalcLabel>
                  <CalcInput placeholder="e.g. Sangeet" value={sel.function_name} onChange={e => patch(sp.id, { function_name: e.target.value })} data-testid={`calc-space-fn-${sp.id}`} />
                </div>
                {!included && (
                  <>
                    <div>
                      <CalcLabel>Booking type</CalcLabel>
                      <select
                        value={sel.mode}
                        onChange={e => patch(sp.id, { mode: e.target.value })}
                        className="w-full bg-white border border-[#E5DCCF] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C6A87C]"
                        data-testid={`calc-space-mode-${sp.id}`}
                      >
                        {sp.time_slots?.length > 0 && <option value="slot">Time slot</option>}
                        {Number(sp.full_day_price) > 0 && <option value="full_day">Full day</option>}
                        {Number(sp.price_per_hour) > 0 && <option value="hour">Per hour</option>}
                      </select>
                    </div>
                    {sel.mode === 'slot' && (
                      <div>
                        <CalcLabel>Time slot</CalcLabel>
                        <select
                          value={sel.slot_id || ''}
                          onChange={e => patch(sp.id, { slot_id: e.target.value })}
                          className="w-full bg-white border border-[#E5DCCF] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C6A87C]"
                          data-testid={`calc-space-slot-${sp.id}`}
                        >
                          {(sp.time_slots || []).map(ts => <option key={ts.id} value={ts.id}>{ts.label} — {inr(ts.price)}</option>)}
                        </select>
                      </div>
                    )}
                    {sel.mode === 'hour' && (
                      <div>
                        <CalcLabel>Hours</CalcLabel>
                        <Stepper value={sel.hours || 1} onChange={v => patch(sp.id, { hours: v })} min={1} max={24} testId={`calc-space-hours-${sp.id}`} />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const StepRooms = ({ config, selections, setSelections, basics }) => {
  const cats = config.room_categories || [];
  const chosen = selections.rooms || [];
  const pkg = (config.venue_packages || []).find(p => p.id === selections.package_id);
  const includedIds = pkg?.included_room_ids || [];
  const patch = (catId, changes) => {
    const exists = chosen.find(c => c.category_id === catId);
    setSelections({
      ...selections,
      rooms: exists
        ? chosen.map(c => c.category_id === catId ? { ...c, ...changes } : c).filter(c => (c.rooms || 0) > 0 || (c.extra_guests || 0) > 0 || (c.extra_mattresses || 0) > 0)
        : [...chosen, { category_id: catId, rooms: 0, extra_guests: 0, extra_mattresses: 0, ...changes }]
    });
  };

  return (
    <div className="space-y-3" data-testid="calc-step-rooms">
      {cats.map(rc => {
        const sel = chosen.find(c => c.category_id === rc.id) || {};
        const included = includedIds.includes('all') || includedIds.includes(rc.id);
        const stdCap = (sel.rooms || 0) * (rc.standard_occupancy || 2);
        return (
          <div key={rc.id} className="border border-[#E5DCCF] bg-white p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-wvc-display text-lg text-[#0A1710]">{rc.name}</p>
                <p className="text-xs text-[#1A3626]/60 mt-0.5 font-light">
                  {rc.total_rooms} rooms · sleeps {rc.standard_occupancy}{rc.max_occupancy > rc.standard_occupancy ? `–${rc.max_occupancy}` : ''} · {rc.bed_type || ''} bed
                  {(rc.meal_inclusions || []).length > 0 && ` · ${rc.meal_inclusions.join(' + ')} included`}
                </p>
              </div>
              <div className="text-right shrink-0">
                {included
                  ? <span className="text-[10px] uppercase tracking-[0.15em] font-bold bg-[#1A3626] text-white px-2 py-1">Included in package</span>
                  : <p className="font-wvc-display text-[#1A3626]">{inr(rc.price_per_night)}<span className="text-xs">/night</span></p>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div>
                <CalcLabel>Rooms{rc.min_rooms ? ` (min ${rc.min_rooms})` : ''}</CalcLabel>
                <Stepper value={sel.rooms || 0} onChange={v => patch(rc.id, { rooms: v })} min={0} max={rc.total_rooms || 99} testId={`calc-room-count-${rc.id}`} />
              </div>
              {rc.extra_guest_allowed && (
                <div>
                  <CalcLabel>Extra guests ({inr(rc.extra_guest_charge)}/night)</CalcLabel>
                  <Stepper value={sel.extra_guests || 0} onChange={v => patch(rc.id, { extra_guests: v })} min={0} max={(rc.max_extra_guests || 1) * (sel.rooms || 0)} testId={`calc-room-extra-${rc.id}`} />
                </div>
              )}
              {Number(rc.extra_mattress_charge) > 0 && (
                <div>
                  <CalcLabel>Extra mattresses ({inr(rc.extra_mattress_charge)}/night)</CalcLabel>
                  <Stepper value={sel.extra_mattresses || 0} onChange={v => patch(rc.id, { extra_mattresses: v })} min={0} testId={`calc-room-mattress-${rc.id}`} />
                </div>
              )}
            </div>
            {(sel.rooms || 0) > 0 && basics.overnight_guests > 0 && (
              <p className={`text-[11px] mt-3 ${basics.overnight_guests > stdCap + (sel.extra_guests || 0) ? 'text-amber-700' : 'text-[#1A3626]/50'}`} data-testid={`calc-room-capacity-${rc.id}`}>
                Accommodates {stdCap}{(sel.extra_guests || 0) > 0 ? ` + ${sel.extra_guests} extra` : ''} of your {basics.overnight_guests} overnight guests.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
